import { CloudDone, CloudOutlined, Edit } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import {
  extractSectionFromChapterSectionHeading,
  spliceChapterSectionIntoSource,
} from '@shared/ast/extractChapterSection'
import { chapterSectionKey } from '@shared/chapterSectionsOrder'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import { LexicalEditor, type LexicalEditorRef } from '../../../components/editor/lexical'
import { Book, ContentFile, apiService } from '../../../services/api'
import { PAPER_BACKGROUND, PAPER_TEXTURE_OVERLAY } from '../../../styles/paperBackground'
import { ContentAST } from '../../../transforms/ast'
import { logger } from '../../../utils/logger'
import type { ChapterSectionEntry } from '../hooks/useChapterSectionsBook'

const AUTOSAVE_MS = 3000
// Generous margin so a row mounts before it scrolls into view and stays mounted
// for a screenful past the edges — avoids a flash of placeholder while scrolling.
const MOUNT_ROOT_MARGIN = '1200px 0px'

type SliceState = { ast: ContentAST | null; loading: boolean; error: string | null }

interface ContinuousPlacedSectionsViewProps {
  bookId: string
  book: Book | null
  /** Placed sections resolved from `flatSectionsOrder`, already in reading order. */
  placedEntries: ChapterSectionEntry[]
  /** Fired after a section's source file is saved, so the parent can refresh state. */
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
}

/**
 * Whole-book continuous editing surface for the flat book structure: every placed
 * section, in `flatSectionsOrder`, stacked with a separator header. Sections render
 * read-only and lazily (mounted only near the viewport); clicking one promotes it to
 * an editable Lexical editor. Only one section is editable at a time. Each section
 * saves back into its own source file (extract → splice → updateChapter), so two
 * placed sections that live in the same source file stay consistent.
 */
export default function ContinuousPlacedSectionsView({
  bookId,
  book,
  placedEntries,
  onSectionContentSaved,
}: ContinuousPlacedSectionsViewProps) {
  const navigate = useNavigate()
  const activeEditorRef = useRef<LexicalEditorRef | null>(null)
  const bookDirection: 'ltr' | 'rtl' = book?.metadata?.direction === 'rtl' ? 'rtl' : 'ltr'

  const entryByKey = useMemo(() => {
    const m = new Map<string, ChapterSectionEntry>()
    for (const e of placedEntries) {
      m.set(chapterSectionKey(e.chapter.filename, e.headingBlockId), e)
    }
    return m
  }, [placedEntries])

  const orderedKeys = useMemo(
    () => placedEntries.map((e) => chapterSectionKey(e.chapter.filename, e.headingBlockId)),
    [placedEntries],
  )

  const [slices, setSlices] = useState<Map<string, SliceState>>(new Map())
  // Bumped per-key to force read-only rows of an edited file to remount with fresh content.
  const [versionByKey, setVersionByKey] = useState<Map<string, number>>(new Map())
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const sourceCacheRef = useRef<Map<string, ContentFile>>(new Map())
  const inflightFilesRef = useRef<Set<string>>(new Set())

  // Active-section edit/save bookkeeping.
  const activeKeyRef = useRef<string | null>(null)
  const dirtyRef = useRef(false)
  const latestAstRef = useRef<ContentAST | null>(null)
  const saveTimerRef = useRef<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [recentlySaved, setRecentlySaved] = useState(false)
  const recentlySavedTimerRef = useRef<number | null>(null)

  useEffect(() => {
    activeKeyRef.current = activeKey
  }, [activeKey])

  const setSlice = useCallback((key: string, next: SliceState) => {
    setSlices((prev) => {
      const m = new Map(prev)
      m.set(key, next)
      return m
    })
  }, [])

  const bumpVersion = useCallback((keys: string[]) => {
    setVersionByKey((prev) => {
      const m = new Map(prev)
      for (const k of keys) {
        m.set(k, (m.get(k) ?? 0) + 1)
      }
      return m
    })
  }, [])

  // Extract every placed section that lives in `filename` from a freshly-loaded source.
  const sliceFileIntoSections = useCallback(
    (filename: string, source: ContentFile) => {
      const ast = source.authorContent
      for (const key of orderedKeys) {
        const entry = entryByKey.get(key)
        if (!entry || entry.chapter.filename !== filename) {
          continue
        }
        if (!ast) {
          setSlice(key, { ast: null, loading: false, error: 'Section has no content yet' })
          continue
        }
        const result = extractSectionFromChapterSectionHeading(ast, entry.headingBlockId)
        setSlice(
          key,
          result
            ? { ast: result.slice, loading: false, error: null }
            : { ast: null, loading: false, error: 'Section not found in source file' },
        )
      }
    },
    [orderedKeys, entryByKey, setSlice],
  )

  const ensureLoaded = useCallback(
    (key: string) => {
      const existing = slices.get(key)
      if (existing && (existing.ast || existing.loading)) {
        return
      }
      const entry = entryByKey.get(key)
      if (!entry || !bookId) {
        return
      }
      const filename = entry.chapter.filename

      const cached = sourceCacheRef.current.get(filename)
      if (cached) {
        sliceFileIntoSections(filename, cached)
        return
      }
      if (inflightFilesRef.current.has(filename)) {
        setSlice(key, { ast: null, loading: true, error: null })
        return
      }

      inflightFilesRef.current.add(filename)
      setSlice(key, { ast: null, loading: true, error: null })
      ;(async () => {
        try {
          const res = await apiService.getChapter(bookId, filename)
          if (res.success && res.data) {
            sourceCacheRef.current.set(filename, res.data)
            sliceFileIntoSections(filename, res.data)
          } else {
            setSlice(key, { ast: null, loading: false, error: 'Failed to load section' })
          }
        } catch (err) {
          logger.warn('Continuous section load failed', err)
          setSlice(key, { ast: null, loading: false, error: 'Failed to load section' })
        } finally {
          inflightFilesRef.current.delete(filename)
        }
      })()
    },
    [slices, entryByKey, bookId, sliceFileIntoSections, setSlice],
  )

  const clearSaveTimer = useCallback(() => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
  }, [])

  // Persist the active section into its source file. Returns true on success.
  const flushSave = useCallback(async (): Promise<boolean> => {
    clearSaveTimer()
    const key = activeKeyRef.current
    const ast = latestAstRef.current
    if (!key || !dirtyRef.current || !ast || !bookId) {
      return true
    }
    const entry = entryByKey.get(key)
    if (!entry) {
      return false
    }
    const filename = entry.chapter.filename
    const headingBlockId = entry.headingBlockId

    setSaving(true)
    try {
      const res = await apiService.getChapter(bookId, filename)
      if (!res.success || !res.data?.authorContent) {
        toast.error('Failed to load source for save')
        return false
      }
      const nextSource = spliceChapterSectionIntoSource(res.data.authorContent, headingBlockId, ast.blocks)
      if (!nextSource) {
        toast.error('Section heading no longer found in source — save aborted')
        return false
      }
      const saveRes = await apiService.updateChapter(
        bookId,
        filename,
        { authorContent: nextSource },
        { returnFull: true },
      )
      if (!saveRes.success || !saveRes.data) {
        toast.error('Failed to save section')
        return false
      }
      dirtyRef.current = false
      sourceCacheRef.current.set(filename, saveRes.data)
      // Refresh slices + remount read-only siblings in the same file.
      sliceFileIntoSections(filename, saveRes.data)
      bumpVersion(orderedKeys.filter((k) => entryByKey.get(k)?.chapter.filename === filename && k !== key))
      onSectionContentSaved?.(filename, saveRes.data)
      setRecentlySaved(true)
      if (recentlySavedTimerRef.current) {
        window.clearTimeout(recentlySavedTimerRef.current)
      }
      recentlySavedTimerRef.current = window.setTimeout(() => setRecentlySaved(false), 1500)
      return true
    } catch (err) {
      logger.error('Continuous section save failed', err)
      toast.error('Failed to save section')
      return false
    } finally {
      setSaving(false)
    }
  }, [bookId, entryByKey, orderedKeys, sliceFileIntoSections, bumpVersion, onSectionContentSaved, clearSaveTimer])

  const openEmbedSource = useCallback(
    async (filename: string, blockId: string) => {
      activeEditorRef.current?.flushPendingSyncToParent()
      if (await flushSave()) {
        navigate(`/edit/${bookId}/${encodeURIComponent(filename)}#${blockId}`)
      }
    },
    [bookId, flushSave, navigate],
  )

  const activate = useCallback(
    async (key: string) => {
      if (key === activeKeyRef.current) {
        return
      }
      // Flush the previously-active section before switching focus.
      const ok = await flushSave()
      if (!ok && !window.confirm('This section failed to save. Switch anyway and discard the change?')) {
        return
      }
      clearSaveTimer()
      dirtyRef.current = false
      latestAstRef.current = slices.get(key)?.ast ?? null
      setRecentlySaved(false)
      setActiveKey(key)
    },
    [flushSave, slices, clearSaveTimer],
  )

  const handleActiveChange = useCallback(
    (ast: ContentAST) => {
      latestAstRef.current = ast
      dirtyRef.current = true
      setRecentlySaved(false)
      clearSaveTimer()
      saveTimerRef.current = window.setTimeout(() => {
        void flushSave()
      }, AUTOSAVE_MS)
    },
    [flushSave, clearSaveTimer],
  )

  const handleActiveSave = useCallback(
    (ast: ContentAST) => {
      latestAstRef.current = ast
      dirtyRef.current = true
      void flushSave()
    },
    [flushSave],
  )

  // Keep a live handle to the latest flushSave so the unmount effect can flush
  // without re-running (and prematurely flushing) on every dependency change.
  const flushSaveRef = useRef(flushSave)
  useEffect(() => {
    flushSaveRef.current = flushSave
  }, [flushSave])

  // Flush on unmount (leaving continuous mode).
  useEffect(() => {
    return () => {
      clearSaveTimer()
      if (recentlySavedTimerRef.current) {
        window.clearTimeout(recentlySavedTimerRef.current)
      }
      void flushSaveRef.current()
    }
  }, [clearSaveTimer])

  if (placedEntries.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Typography sx={{ color: 'var(--text-secondary)', textAlign: 'center', maxWidth: 420 }}>
          No placed sections yet. Place sections into the reading order from the Sections view (drag into Placed) or the
          Organize dialog, then they'll appear here.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        bgcolor: 'var(--bg-app)',
        py: { xs: 0, md: 2 },
        direction: bookDirection === 'rtl' ? 'ltr' : undefined,
      }}
    >
      <Box
        sx={{
          maxWidth: '816px',
          width: '100%',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          direction: bookDirection,
        }}
      >
        {orderedKeys.map((key) => {
          const entry = entryByKey.get(key)
          if (!entry) {
            return null
          }
          return (
            <SectionRow
              key={key}
              bookId={bookId}
              activeEditorRef={activeEditorRef}
              onOpenEmbedSource={openEmbedSource}
              sectionKey={key}
              entry={entry}
              bookDirection={bookDirection}
              isActive={activeKey === key}
              slice={slices.get(key) ?? null}
              version={versionByKey.get(key) ?? 0}
              bookSettings={book?.settings}
              saving={saving}
              recentlySaved={recentlySaved}
              dirty={activeKey === key && dirtyRef.current}
              onNeedContent={ensureLoaded}
              onActivate={activate}
              onActiveChange={handleActiveChange}
              onActiveSave={handleActiveSave}
            />
          )
        })}
      </Box>
    </Box>
  )
}

interface SectionRowProps {
  bookId: string
  activeEditorRef: React.RefObject<LexicalEditorRef>
  onOpenEmbedSource: (filename: string, blockId: string) => void
  sectionKey: string
  entry: ChapterSectionEntry
  bookDirection: 'ltr' | 'rtl'
  isActive: boolean
  slice: SliceState | null
  version: number
  bookSettings: Book['settings'] | undefined
  saving: boolean
  recentlySaved: boolean
  dirty: boolean
  onNeedContent: (key: string) => void
  onActivate: (key: string) => void
  onActiveChange: (ast: ContentAST) => void
  onActiveSave: (ast: ContentAST) => void
}

function SectionRow({
  bookId,
  activeEditorRef,
  onOpenEmbedSource,
  sectionKey,
  entry,
  bookDirection,
  isActive,
  slice,
  version,
  bookSettings,
  saving,
  recentlySaved,
  dirty,
  onNeedContent,
  onActivate,
  onActiveChange,
  onActiveSave,
}: SectionRowProps) {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [near, setNear] = useState(false)
  const lastHeightRef = useRef<number>(280)

  // Lazily mount content when the row scrolls near the viewport; unmount when far
  // (the active section always stays mounted regardless).
  useEffect(() => {
    const el = contentRef.current
    if (!el) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const isNear = entries[0]?.isIntersecting ?? false
        setNear(isNear)
      },
      { root: null, rootMargin: MOUNT_ROOT_MARGIN },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const mounted = near || isActive

  useEffect(() => {
    if (mounted) {
      onNeedContent(sectionKey)
    }
  }, [mounted, sectionKey, onNeedContent])

  // Remember the last rendered height so the placeholder keeps scroll position stable.
  useEffect(() => {
    if (!mounted || !contentRef.current) {
      return
    }
    const el = contentRef.current
    const ro = new ResizeObserver(() => {
      if (el.offsetHeight > 0) {
        lastHeightRef.current = el.offsetHeight
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [mounted])

  const published = Boolean(entry.chapter.publishedSections?.includes(entry.headingBlockId))

  return (
    <Box
      sx={{
        border: '1px solid var(--toolbar-page-border)',
        background: PAPER_BACKGROUND,
        '&::after': PAPER_TEXTURE_OVERLAY,
        position: 'relative',
        ...(isActive && { boxShadow: '0 0 0 2px var(--border-primary)' }),
      }}
    >
      {/* Separator header */}
      <Box
        onClick={isActive ? undefined : () => onActivate(sectionKey)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 1.5, md: 2 },
          py: 1,
          borderBottom: '1px solid var(--toolbar-page-border)',
          bgcolor: 'var(--bg-surface-subtle)',
          cursor: isActive ? 'default' : 'pointer',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {entry.displayText || entry.text || 'Untitled section'}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {entry.chapter.title || entry.chapter.filename}
          </Typography>
        </Box>
        {isActive ? (
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
            {saving ? 'Saving…' : dirty ? 'Editing' : recentlySaved ? 'Saved' : 'Editing'}
          </Typography>
        ) : (
          <Edit sx={{ fontSize: '1rem', color: 'var(--text-secondary)', flexShrink: 0, opacity: 0.6 }} />
        )}
        {published ? (
          <CloudDone sx={{ fontSize: '1.1rem', color: 'success.main', flexShrink: 0 }} />
        ) : (
          <CloudOutlined sx={{ fontSize: '1.1rem', color: 'var(--text-secondary)', flexShrink: 0, opacity: 0.5 }} />
        )}
      </Box>

      {/* Content */}
      <Box
        ref={contentRef}
        onClick={isActive || !slice?.ast ? undefined : () => onActivate(sectionKey)}
        sx={{
          minHeight: mounted ? undefined : lastHeightRef.current,
          cursor: !isActive && slice?.ast ? 'pointer' : 'default',
        }}
      >
        {!mounted ? null : slice?.loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
            <CircularProgress size={20} />
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Loading…</Typography>
          </Box>
        ) : slice?.error ? (
          <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'error.main', fontSize: '0.9rem' }}>{slice.error}</Typography>
          </Box>
        ) : slice?.ast ? (
          <LexicalEditor
            key={isActive ? `${sectionKey}-edit` : `${sectionKey}-read-v${version}`}
            ref={isActive ? activeEditorRef : undefined}
            bookId={bookId}
            chapterId={entry.chapter.filename}
            onOpenEmbedSource={onOpenEmbedSource}
            initialContent={slice.ast}
            readOnly={!isActive}
            readerView={false}
            showToolbar={isActive}
            showSaveButton={isActive}
            isAuthor={isActive}
            placeholder=""
            bookSettings={bookSettings}
            currentDirection={bookDirection}
            onChange={isActive ? onActiveChange : undefined}
            onSave={isActive ? onActiveSave : undefined}
            saving={saving}
            hasUnsavedChanges={isActive && dirty}
            recentlySaved={isActive && recentlySaved}
            style={{ minHeight: isActive ? '300px' : undefined, border: 'none' }}
          />
        ) : null}
      </Box>
    </Box>
  )
}
